/**
 * Authentication Store
 * Manages user authentication state using Zustand
 */

import { create } from 'zustand';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
} from 'firebase/auth';
import { auth } from '../services/firebase';

// Whitelisted admin emails (allowed to bypass domain restriction)
const WHITELISTED_EMAILS = [
  'michelle.armirola@gmail.com',
];

const isAllowedEmail = (email) => {
  return email.endsWith('@fusionict.com') || WHITELISTED_EMAILS.includes(email.toLowerCase());
};

export const useAuthStore = create((set) => ({
  user: null,
  loading: true,
  error: null,

  // Initialize auth listener
  initialize: () => {
    if (!auth) {
      // No Firebase configured - set loading to false
      set({ loading: false, error: 'Firebase not configured' });
      return () => {}; // Return empty cleanup function
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      set({ user, loading: false });
    });
    return unsubscribe;
  },

  // Sign in with email and password
  signIn: async (email, password) => {
    if (!auth) {
      const error = 'Firebase not configured. Please set up Firebase to enable authentication.';
      set({ error, loading: false });
      throw new Error(error);
    }

    try {
      set({ loading: true, error: null });
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      set({ user: userCredential.user, loading: false });
      return userCredential.user;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Register new user
  signUp: async (email, password) => {
    if (!auth) {
      const error = 'Firebase not configured. Please set up Firebase to enable authentication.';
      set({ error, loading: false });
      throw new Error(error);
    }

    try {
      set({ loading: true, error: null });
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      set({ user: userCredential.user, loading: false });
      return userCredential.user;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Sign in with Google (restricted to fusionict.com domain + whitelisted emails)
  signInWithGoogle: async () => {
    if (!auth) {
      const error = 'Firebase not configured. Please set up Firebase to enable authentication.';
      set({ error, loading: false });
      throw new Error(error);
    }

    try {
      set({ loading: true, error: null });
      const provider = new GoogleAuthProvider();
      // Allow account selection for whitelisted users
      provider.setCustomParameters({
        prompt: 'select_account',
      });

      const result = await signInWithPopup(auth, provider);

      // Check if email is allowed (fusionict.com domain or whitelisted)
      const email = result.user.email;
      if (!isAllowedEmail(email)) {
        await firebaseSignOut(auth);
        const error = 'Access restricted. Contact admin for access.';
        set({ error, loading: false, user: null });
        throw new Error(error);
      }

      set({ user: result.user, loading: false });
      return result.user;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Sign out
  signOut: async () => {
    if (!auth) {
      set({ user: null, loading: false });
      return;
    }

    try {
      set({ loading: true, error: null });
      await firebaseSignOut(auth);
      set({ user: null, loading: false });
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Clear error
  clearError: () => set({ error: null }),
}));
